/**
 * Price history for the game view: which window to ask for, how the API's per-position points
 * become one line per side, and the open → now facts the chart states. Pure, like board.ts —
 * the chart component only draws what this returns.
 */
import type { SideBook } from "./board";
import type { PricePoint } from "./types";

export type HistoryRange = "24h" | "7d" | "all";

const HOUR = 3_600_000;
const DAY = 24 * HOUR;

export const HISTORY_RANGES: { value: HistoryRange; label: string; ms: number }[] = [
  { value: "24h", label: "24H", ms: DAY },
  { value: "7d", label: "7D", ms: 7 * DAY },
  // Most games list well inside a month; the API buckets this window by the day or four hours.
  { value: "all", label: "All", ms: 30 * DAY },
];

/**
 * The window to request, with `to` floored to the minute so repeated reads ask the same question
 * (and signed-out visitors share one edge-cache entry).
 */
export function historyWindow(range: HistoryRange, now: number = Date.now()): { from: number; to: number } {
  const to = Math.floor(now / 60_000) * 60_000;
  const ms = HISTORY_RANGES.find((r) => r.value === range)?.ms ?? DAY;
  return { from: to - ms, to };
}

export interface SeriesPoint {
  t: number;
  price: number;
}

export interface SideSeries {
  sideKey: string;
  label: string;
  points: SeriesPoint[];
}

/**
 * One series per side of a book. A side can stand on more than one position; where two share a
 * timestamp the lower price wins, the same "best = lowest probability" rule as the board. Prices
 * outside (0, 1) are empty books, not prices, and are dropped.
 */
export function sideSeries(points: PricePoint[], sides: SideBook[]): SideSeries[] {
  const sideOf = new Map<string, string>();
  for (const side of sides) for (const hash of side.positionHashes ?? []) sideOf.set(hash, side.sideKey);

  const byside = new Map<string, Map<number, number>>();
  for (const p of points) {
    const key = sideOf.get(p.position_hash);
    if (!key || !(p.price > 0 && p.price < 1) || !Number.isFinite(p.t)) continue;
    let series = byside.get(key);
    if (!series) byside.set(key, (series = new Map()));
    const cur = series.get(p.t);
    if (cur === undefined || p.price < cur) series.set(p.t, p.price);
  }

  return sides.map((side) => ({
    sideKey: side.sideKey,
    label: side.label,
    points: [...(byside.get(side.sideKey) ?? new Map<number, number>())]
      .map(([t, price]) => ({ t, price }))
      .sort((a, b) => a.t - b.t),
  }));
}

export interface Move {
  open: SeriesPoint;
  now: SeriesPoint;
  high: SeriesPoint;
  low: SeriesPoint;
  /** now − open, in percentage points of implied chance (0.55 → 0.60 is +5). */
  chancePts: number;
}

/** Where a side started in the window, where it is now, and its range. Null without two points. */
export function sideMove(series: SideSeries): Move | null {
  const pts = series.points;
  if (pts.length < 2) return null;
  let high = pts[0];
  let low = pts[0];
  for (const p of pts) {
    if (p.price > high.price) high = p;
    if (p.price < low.price) low = p;
  }
  const open = pts[0];
  const now = pts[pts.length - 1];
  return { open, now, high, low, chancePts: Math.round((now.price - open.price) * 1000) / 10 };
}

/**
 * The side that moved most, for the headline. Moving toward a side (its chance rising) and away
 * from the other are the same move on a two-way book, so the biggest absolute change is enough.
 */
export function biggestMove(series: SideSeries[]): { series: SideSeries; move: Move } | null {
  let out: { series: SideSeries; move: Move } | null = null;
  for (const s of series) {
    const move = sideMove(s);
    if (move && (!out || Math.abs(move.chancePts) > Math.abs(out.move.chancePts))) out = { series: s, move };
  }
  return out;
}
